/* ──────────────────────────────────────────────────────────────────────────
   Le journal (blog) — assets/journal.js

   Un seul élément, <journal-page>, posé par les deux sections du journal.
   Quatre choses, et rien d'autre :
   1. la hauteur réelle de l'en-tête collé, pour caler les pastilles et la
      colonne latérale juste dessous (même dispositif que collection-v2.js) ;
   2. « Voir plus d'articles » sans rechargement : la page suivante est
      demandée à l'adresse que Liquid a rendue (`paginate.next.url`), jamais à
      une adresse reconstruite ici — l'anglais vit sous /en-eu, /en-ca… et une
      adresse recollée en JS perdrait la langue. Sans JS, le lien reste un
      lien vers la page 2 ;
   3. « Copier le lien », annoncé aux lecteurs d'écran ;
   4. les vidéos YouTube, chargées seulement au clic : avant, la page ne
      demande rien à YouTube (snippets/journal-youtube.liquid).
   ────────────────────────────────────────────────────────────────────────── */
(function () {
  if (customElements.get('journal-page')) return;

  /* L'en-tête collé n'est mesuré qu'une fois par page, même si les deux
     sections du journal y figurent. */
  var enteteSuivi = false;
  function suivreEntete() {
    if (enteteSuivi) return;
    enteteSuivi = true;
    /* Seule la section que Dawn a rendue collante porte
       `.shopify-section-header-sticky` (voir collection-v2.js). */
    var entete = document.querySelector('.shopify-section-header-sticky');
    if (!entete || !window.ResizeObserver) return;
    var observateur = new ResizeObserver(function () {
      document.documentElement.style.setProperty('--header-height-reel', entete.getBoundingClientRect().height + 'px');
    });
    observateur.observe(entete);
  }

  class JournalPage extends HTMLElement {
    connectedCallback() {
      suivreEntete();
      this.lienSuite = this.querySelector('[data-journal-suite]');
      if (this.lienSuite) {
        this.lienSuite.addEventListener('click', this.chargerSuite.bind(this));
      }
      /* Deux boutons « Copier le lien » (partage mobile et desktop), un seul
         affiché : chacun annonce dans son propre bloc. */
      var copiers = this.querySelectorAll('[data-journal-copier]');
      for (var i = 0; i < copiers.length; i++) {
        copiers[i].addEventListener('click', this.copierLien.bind(this, copiers[i]));
      }
      var videos = this.querySelectorAll('[data-journal-youtube]');
      for (var j = 0; j < videos.length; j++) {
        videos[j].addEventListener('click', this.lancerVideo.bind(this, videos[j]));
      }
    }

    /* Le lien vers YouTube devient le lecteur, à sa place. Cmd/Ctrl-clic garde
       le comportement d'un lien : la vidéo s'ouvre dans un onglet. */
    lancerVideo(lien, evt) {
      if (evt.metaKey || evt.ctrlKey || evt.shiftKey) return;
      evt.preventDefault();
      var lecteur = document.createElement('iframe');
      /* youtube-nocookie : pas de cookie YouTube tant que la vidéo ne joue pas.
         `autoplay` répond au clic ; sur iPhone, un second appui peut rester
         nécessaire. */
      lecteur.src = 'https://www.youtube-nocookie.com/embed/' + encodeURIComponent(lien.dataset.journalYoutube) +
        '?autoplay=1&rel=0&playsinline=1';
      lecteur.title = lien.dataset.titre || '';
      lecteur.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
      lecteur.allowFullscreen = true;
      /* YouTube exige l'origine de la page : sans elle, le lecteur s'affiche
         en erreur au lieu de jouer. */
      lecteur.referrerPolicy = 'strict-origin-when-cross-origin';
      lecteur.className = 'j-yt__lecteur';
      lien.replaceWith(lecteur);
      /* Au clavier, on reprend dans le lecteur, pas en haut de la page. */
      lecteur.focus();
    }

    chargerSuite(evt) {
      var lien = this.lienSuite;
      var grille = this.querySelector('[data-journal-grille]');
      if (!grille || !window.fetch || !window.DOMParser) return;
      evt.preventDefault();
      if (lien.getAttribute('aria-busy') === 'true') return;

      var libelle = lien.textContent;
      lien.setAttribute('aria-busy', 'true');
      lien.textContent = lien.dataset.chargement || libelle;
      var compte = this.querySelector('[data-journal-compte]');
      var self = this;

      fetch(lien.href, { headers: { Accept: 'text/html' } })
        .then(function (rep) {
          if (!rep.ok) throw new Error(rep.status);
          return rep.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var cartes = doc.querySelectorAll('[data-journal-grille] > [data-journal-carte]');
          /* Les cartes ajoutées passent derrière la rangée des parfums : leur
             rang reprend là où la grille s'arrête. */
          var rang = 100 + grille.querySelectorAll('[data-journal-carte]').length * 2;
          var premiere = null;
          for (var i = 0; i < cartes.length; i++) {
            var carte = document.importNode(cartes[i], true);
            carte.style.setProperty('--j-rang', rang + i * 2);
            grille.appendChild(carte);
            if (!premiere) premiere = carte;
          }
          var suivant = doc.querySelector('[data-journal-suite]');
          var compteNeuf = doc.querySelector('[data-journal-compte]');
          if (compte && compteNeuf) compte.textContent = compteNeuf.textContent;
          if (suivant) {
            lien.href = suivant.getAttribute('href');
            lien.textContent = libelle;
            lien.removeAttribute('aria-busy');
          } else {
            /* Dernière page : plus de suite, le compteur dit « 7 articles sur 7 ». */
            lien.remove();
            self.lienSuite = null;
          }
          /* Le focus va sur la première carte ajoutée : au clavier, on reprend
             la lecture là où la liste s'est allongée. */
          if (premiere) premiere.focus({ preventScroll: false });
        })
        .catch(function () {
          lien.textContent = libelle;
          lien.removeAttribute('aria-busy');
          if (compte && lien.dataset.erreur) compte.textContent = lien.dataset.erreur;
        });
    }

    copierLien(bouton) {
      var url = bouton.dataset.url || window.location.href;
      var texte = bouton.querySelector('[data-journal-copier-texte]');
      var bloc = bouton.closest('.j-partage');
      var statut = bloc && bloc.querySelector('[data-journal-statut]');
      if (!navigator.clipboard) return;
      navigator.clipboard.writeText(url).then(function () {
        if (!texte) return;
        var avant = texte.textContent;
        texte.textContent = bouton.dataset.copie;
        if (statut) statut.textContent = bouton.dataset.copie;
        setTimeout(function () {
          texte.textContent = avant;
          if (statut) statut.textContent = '';
        }, 2500);
      });
    }
  }

  customElements.define('journal-page', JournalPage);
})();
